import { useState, useRef, useMemo, useEffect } from 'react'
import {
    Camera, Check, Headphones, ImagePlus, Loader2, Mic, MicOff,
    MonitorPlay, MonitorX, Pencil, PhoneOff, Settings2, Trash2, Video,
    VideoOff, Volume2, VolumeX, Wifi
} from 'lucide-react'
import type { ShareKind } from '@shared/protocol'
import { MriInput, MriPopover, MriPopoverContent, MriPopoverTrigger, cn } from '@mriqbox/ui-kit'
import { Avatar } from '@/components/Avatar'
import { processAvatarFile } from '@/lib/avatar'
import {
    QUALITY_PRESETS, RESOLUTION_ORDER, RESOLUTION_PRESETS, listCameras,
    type ShareQuality, type ShareResolution
} from '@/lib/screenCapture'
import { listMicrophones, microphoneSupported } from '@/lib/voiceCapture'
import { ENCODER_CHOICES, ENCODER_ORDER, type EncoderChoice } from '@/lib/videoEncoder'
import { RECEIVE_ORDER, RECEIVE_PRESETS, type ReceivePreference } from '@/lib/receiveQuality'
import { UploadLimitMenu } from '@/components/UploadLimitMenu'
import type { UploadLimit } from '@/lib/uploadLimit'
import { BACKGROUND_PRESETS, readBackgroundFile } from '@/lib/backgrounds'
import type { BackgroundEffect } from '@/lib/videoEffects'

export interface DiscordVoicePanelProps {
    code: string
    roomName?: string
    /** Sub-sala em que estou. Ausente na sala principal. */
    channelName?: string
    nickname: string
    selfId: string | null
    avatar?: string | null
    isSpeaking: boolean
    sharingKinds: Set<ShareKind>
    canShareScreen: boolean
    canUseCamera: boolean
    quality: ShareQuality
    resolution: ShareResolution
    /** Placa de video ou processador codificando o que sai daqui. */
    encoder: EncoderChoice
    /** O que esta codificando de verdade, quando ha o que dizer. */
    encoderNote?: string | null
    cameraId: string | null
    background: BackgroundEffect
    effectLoading: boolean
    inVoice: boolean
    muted: boolean
    deafened: boolean
    /** A tela no ar esta mandando som? Sem som, nao ha o que cortar. */
    screenHasAudio?: boolean
    /** O som da tela que estou mandando esta cortado. */
    screenAudioMuted?: boolean
    onToggleScreenAudio?: () => void
    voiceLoading: boolean
    micId: string | null
    noiseSuppression: boolean
    onToggleShare: (kind: ShareKind) => void
    onToggleVoice: () => void
    onToggleMute: () => void
    onToggleDeafen: () => void
    onMicChange: (deviceId: string) => void
    onCameraChange: (deviceId: string) => void
    onQualityChange: (quality: ShareQuality) => void
    onResolutionChange: (resolution: ShareResolution) => void
    onEncoderChange: (encoder: EncoderChoice) => void
    receivePreference?: ReceivePreference
    onReceivePreferenceChange?: (preference: ReceivePreference) => void
    uploadLimit?: UploadLimit
    onUploadLimitChange?: (limit: UploadLimit) => void
    onBackgroundChange: (effect: BackgroundEffect) => void
    onNoiseSuppressionChange: (enabled: boolean) => void
    onAvatarChange?: (dataUrl: string | null) => void
    onRename: (nickname: string) => void
    onLeave: () => void
    className?: string
}

export function DiscordVoicePanel({
    code,
    roomName,
    channelName,
    nickname,
    isSpeaking,
    sharingKinds,
    canShareScreen,
    canUseCamera,
    quality,
    resolution,
    encoder,
    encoderNote = null,
    cameraId,
    background,
    effectLoading,
    inVoice,
    muted,
    deafened,
    screenHasAudio = false,
    screenAudioMuted = false,
    onToggleScreenAudio,
    voiceLoading,
    micId,
    noiseSuppression,
    avatar,
    onAvatarChange,
    onToggleShare,
    onToggleVoice,
    onToggleMute,
    onToggleDeafen,
    onMicChange,
    onCameraChange,
    onQualityChange,
    onResolutionChange,
    onEncoderChange,
    receivePreference = 'auto',
    onReceivePreferenceChange,
    uploadLimit = 'none',
    onUploadLimitChange,
    onBackgroundChange,
    onNoiseSuppressionChange,
    onRename,
    onLeave,
    className
}: DiscordVoicePanelProps) {
    const sharingScreen = sharingKinds.has('screen')
    const sharingCamera = sharingKinds.has('camera')
    const [editingNickname, setEditingNickname] = useState(false)
    const [draftNick, setDraftNick] = useState(nickname)
    const fileRef = useRef<HTMLInputElement>(null)
    const [uploadError, setUploadError] = useState<string | null>(null)
    const canUseVoice = useMemo(() => microphoneSupported(), [])

    const microphones = useMicrophones(inVoice)
    const cameras = useCameras(sharingCamera)

    const handleAvatarFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        try {
            const dataUrl = await processAvatarFile(file)
            onAvatarChange?.(dataUrl)
            setUploadError(null)
        } catch (err) {
            // O estado existia e nunca era preenchido: a foto simplesmente nao
            // aparecia e a pessoa nao tinha como saber por que.
            setUploadError(err instanceof Error ? err.message : 'Nao consegui usar essa imagem.')
        }
        if (e.target) e.target.value = ''
    }

    const handleSaveNick = () => {
        const clean = draftNick.trim()
        if (clean && clean !== nickname) onRename(clean)
        setEditingNickname(false)
    }

    return (
        <div className={cn('border-t border-border/70 bg-card/85 backdrop-blur-md select-none', className)}>
            {/* 1. Status de Conexão e Botão Desconectar (Estilo Discord) */}
            <div className="flex items-center justify-between border-b border-border/40 bg-background/40 px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-400">
                        <Wifi className="h-3.5 w-3.5" />
                    </span>
                    <div className="min-w-0">
                        <p className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-400 leading-none">
                            Voz conectada
                        </p>
                        {/* Numa sub-sala, e ela que importa: e so com quem
                            esta la que a voz e a tela trafegam. */}
                        <p className="font-mono text-[10px] text-muted-foreground truncate mt-0.5">
                            {channelName
                                ? `${roomName ?? code} / ${channelName}`
                                : roomName ? `${roomName} / ${code}` : `Sala / ${code}`}
                        </p>
                    </div>
                </div>

                <button
                    type="button"
                    title="Desconectar da sala"
                    aria-label="Desconectar da sala"
                    onClick={onLeave}
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/20 hover:text-destructive transition-colors"
                >
                    <PhoneOff className="h-4 w-4" />
                </button>
            </div>

            {/* 2. Grid de Ações Rápidas (Câmera e Compartilhar Tela) */}
            <div className="grid grid-cols-2 gap-1.5 p-2 border-b border-border/40">
                <button
                    type="button"
                    onClick={() => onToggleShare('camera')}
                    disabled={!canUseCamera}
                    title={sharingCamera ? 'Desligar câmera' : 'Ligar câmera'}
                    className={cn(
                        'flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg text-xs font-semibold transition-all border',
                        sharingCamera
                            ? 'bg-destructive/15 border-destructive/40 text-destructive hover:bg-destructive/25'
                            : 'bg-background/50 border-border/60 text-muted-foreground hover:text-foreground hover:bg-background'
                    )}
                >
                    {sharingCamera ? <VideoOff className="h-3.5 w-3.5" /> : <Video className="h-3.5 w-3.5" />}
                    <span>{sharingCamera ? 'Câmera On' : 'Câmera'}</span>
                </button>

                <button
                    type="button"
                    onClick={() => onToggleShare('screen')}
                    disabled={!canShareScreen}
                    title={sharingScreen ? 'Parar transmissão' : 'Compartilhar tela'}
                    className={cn(
                        'flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg text-xs font-semibold transition-all border',
                        sharingScreen
                            ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/25'
                            : 'bg-background/50 border-border/60 text-muted-foreground hover:text-foreground hover:bg-background'
                    )}
                >
                    {sharingScreen ? <MonitorX className="h-3.5 w-3.5" /> : <MonitorPlay className="h-3.5 w-3.5" />}
                    <span>{sharingScreen ? 'Transmitindo' : 'Compartilhar'}</span>
                </button>
            </div>

            {/*
              * Cortar o som que EU mando, sem parar a transmissao. So aparece
              * quando a captura veio com audio: sem trilha nao ha o que cortar,
              * e um botao morto ali so confundiria.
              */}
            {sharingScreen && screenHasAudio && (
                <button
                    type="button"
                    onClick={onToggleScreenAudio}
                    aria-pressed={screenAudioMuted}
                    title={screenAudioMuted
                        ? 'Voltar a mandar o som da tela'
                        : 'Parar de mandar o som da tela (a imagem continua)'}
                    className={cn(
                        'flex w-full items-center gap-2 border-b border-border/40 px-2.5 py-1.5 text-[11px] font-medium transition-colors',
                        screenAudioMuted
                            ? 'bg-destructive/10 text-destructive hover:bg-destructive/20'
                            : 'bg-background/30 text-muted-foreground hover:text-foreground hover:bg-background/50'
                    )}
                >
                    {screenAudioMuted
                        ? <VolumeX className="h-3.5 w-3.5 shrink-0" />
                        : <Volume2 className="h-3.5 w-3.5 shrink-0" />}
                    <span className="truncate">
                        {screenAudioMuted ? 'Som da tela cortado' : 'Mandando o som da tela'}
                    </span>
                </button>
            )}

            {/* 3. Barra de Perfil e Controles de Áudio (Microfone, Deafen e Configurações) */}
            <div className="flex items-center justify-between px-2.5 py-2 bg-background/30">
                {editingNickname ? (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault()
                            handleSaveNick()
                        }}
                        className="flex items-center gap-1 flex-1 min-w-0 pr-1"
                    >
                        <MriInput
                            autoFocus
                            size="sm"
                            value={draftNick}
                            maxLength={24}
                            onChange={(e) => setDraftNick(e.target.value)}
                            onBlur={handleSaveNick}
                            className="h-7 text-xs"
                        />
                        <button type="submit" className="text-primary hover:text-primary/80 p-1">
                            <Check className="h-3.5 w-3.5" />
                        </button>
                    </form>
                ) : (
                    <div
                        onClick={() => {
                            setDraftNick(nickname)
                            setEditingNickname(true)
                        }}
                        title="Clique para mudar seu nickname"
                        className="group flex items-center gap-2 min-w-0 flex-1 pr-1 cursor-pointer"
                    >
                        <div className="relative group/avatar shrink-0" title="Trocar foto de perfil" onClick={(e) => e.stopPropagation()}>
                            <Avatar nickname={nickname || '?'} avatar={avatar} size={28} />
                            <label className="absolute inset-0 flex items-center justify-center rounded-full bg-black/60 opacity-0 group-hover/avatar:opacity-100 transition-opacity cursor-pointer">
                                <Camera className="h-3 w-3 text-white" />
                                <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp,image/*"
                                    className="hidden"
                                    onChange={handleAvatarFile}
                                />
                            </label>
                            <span
                                className={cn(
                                    'absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-card',
                                    isSpeaking ? 'bg-primary animate-pulse' : 'bg-emerald-500'
                                )}
                            />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-bold text-foreground leading-none group-hover:text-primary transition-colors flex items-center gap-1">
                                <span className="truncate">{nickname || 'Você'}</span>
                                <Pencil className="h-2.5 w-2.5 opacity-0 group-hover:opacity-100 text-muted-foreground shrink-0" />
                            </p>
                            <p className="text-[10px] text-muted-foreground mt-0.5 truncate">
                                {isSpeaking ? 'Falando...' : inVoice ? (muted ? 'Mutado' : 'Na voz') : 'Conectado'}
                            </p>
                        </div>
                    </div>
                )}

                {/* Ícones de Ação: Microfone, Deafen e Settings */}
                <div className="flex items-center gap-0.5 shrink-0">
                    {/* Microfone */}
                    <button
                        type="button"
                        onClick={inVoice ? onToggleMute : onToggleVoice}
                        disabled={!canUseVoice || voiceLoading}
                        title={
                            voiceLoading
                                ? 'Conectando microfone...'
                                : inVoice
                                    ? (muted ? 'Desmutar microfone' : 'Mutar microfone')
                                    : 'Entrar na voz'
                        }
                        className={cn(
                            'flex h-7.5 w-7.5 items-center justify-center rounded-lg transition-colors',
                            inVoice && muted
                                ? 'bg-destructive/15 text-destructive hover:bg-destructive/25'
                                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        )}
                    >
                        {voiceLoading ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : inVoice && !muted ? (
                            <Mic className="h-3.5 w-3.5 text-primary" />
                        ) : (
                            <MicOff className="h-3.5 w-3.5" />
                        )}
                    </button>

                    {/* Deafen (Silenciar Sala) */}
                    <button
                        type="button"
                        onClick={onToggleDeafen}
                        title={deafened ? 'Voltar a ouvir a sala' : 'Silenciar a sala (Deafen)'}
                        className={cn(
                            'flex h-7.5 w-7.5 items-center justify-center rounded-lg transition-colors',
                            deafened
                                ? 'bg-destructive/15 text-destructive hover:bg-destructive/25'
                                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        )}
                    >
                        {deafened ? <VolumeX className="h-3.5 w-3.5" /> : <Headphones className="h-3.5 w-3.5" />}
                    </button>

                    {/* Popover de Configurações de Dispositivos e Qualidade */}
                    <MriPopover>
                        <MriPopoverTrigger asChild>
                            <button
                                type="button"
                                title="Configurações de voz, vídeo e qualidade"
                                className="flex h-7.5 w-7.5 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                            >
                                <Settings2 className="h-3.5 w-3.5" />
                            </button>
                        </MriPopoverTrigger>
                        <MriPopoverContent
                            align="end"
                            side="top"
                            className="max-h-[min(70vh,32rem)] w-68 overflow-y-auto overscroll-contain p-2.5 shadow-2xl rounded-2xl border-border/80"
                        >
                            {/* Foto de Perfil */}
                            <p className="sticky top-0 z-10 bg-popover px-2 pb-1.5 pt-1 text-[10px] font-bold uppercase tracking-widest text-primary">
                                Foto de Perfil
                            </p>
                            <div className="flex items-center gap-2.5 px-2 py-1.5 mb-2 rounded-xl bg-card/60 border border-border/40">
                                <Avatar nickname={nickname || '?'} avatar={avatar} size={36} />
                                <div className="flex flex-col gap-1 min-w-0 flex-1">
                                    <label className="flex items-center justify-center gap-1.5 px-2 py-1 text-xs font-semibold rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/80 cursor-pointer transition-colors">
                                        <Camera className="h-3 w-3" />
                                        <span>{avatar ? 'Trocar Foto' : 'Enviar Foto'}</span>
                                        <input
                                            type="file"
                                            accept="image/png,image/jpeg,image/webp,image/*"
                                            className="hidden"
                                            onChange={handleAvatarFile}
                                        />
                                    </label>
                                    {uploadError && (
                                        <p role="alert" className="px-2 text-center text-[10px] text-destructive">
                                            {uploadError}
                                        </p>
                                    )}
                                    {avatar && (
                                        <button
                                            type="button"
                                            onClick={() => onAvatarChange?.(null)}
                                            className="flex items-center justify-center gap-1 px-2 py-0.5 text-[10px] text-muted-foreground hover:text-destructive transition-colors"
                                        >
                                            <Trash2 className="h-2.5 w-2.5" />
                                            <span>Remover foto</span>
                                        </button>
                                    )}
                                </div>
                            </div>

                            <p className="sticky top-0 z-10 border-t border-border/60 bg-popover px-2 pb-2 pt-2 text-[10px] font-bold uppercase tracking-widest text-primary">
                                Qualidade da Transmissão
                            </p>
                            {(Object.keys(QUALITY_PRESETS) as ShareQuality[]).map((key) => {
                                const preset = QUALITY_PRESETS[key]
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => onQualityChange(key)}
                                        className={cn(
                                            'w-full rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-accent text-xs',
                                            quality === key && 'bg-accent font-semibold text-primary'
                                        )}
                                    >
                                        <span className="block font-medium text-foreground">{preset.label}</span>
                                        <span className="block text-[10px] text-muted-foreground">{preset.description}</span>
                                    </button>
                                )
                            })}

                            {/* Cadencia acima, tamanho aqui: sao os dois eixos
                                do custo de uma transmissao, e quem transmite e
                                quem sabe qual dos dois pode ceder. */}
                            <p className="sticky top-0 z-10 mt-2.5 border-t border-border/60 bg-popover px-2 pb-1.5 pt-2 text-[10px] font-bold uppercase tracking-widest text-primary">
                                Resolução
                            </p>
                            {RESOLUTION_ORDER.map((key) => {
                                const preset = RESOLUTION_PRESETS[key]
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => onResolutionChange(key)}
                                        aria-pressed={resolution === key}
                                        className={cn(
                                            'w-full rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-accent text-xs',
                                            resolution === key && 'bg-accent font-semibold text-primary'
                                        )}
                                    >
                                        <span className="block font-medium text-foreground">{preset.label}</span>
                                        <span className="block text-[10px] text-muted-foreground">{preset.description}</span>
                                    </button>
                                )
                            })}

                            {/* Cadencia e tamanho dizem quanto codificar; aqui
                                e onde — placa de video ou processador. */}
                            <p className="sticky top-0 z-10 mt-2.5 border-t border-border/60 bg-popover px-2 pb-1.5 pt-2 text-[10px] font-bold uppercase tracking-widest text-primary">
                                Codificação
                            </p>
                            {ENCODER_ORDER.map((key) => {
                                const preset = ENCODER_CHOICES[key]
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => onEncoderChange(key)}
                                        aria-pressed={encoder === key}
                                        className={cn(
                                            'w-full rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-accent text-xs',
                                            encoder === key && 'bg-accent font-semibold text-primary'
                                        )}
                                    >
                                        <span className="block font-medium text-foreground">{preset.label}</span>
                                        <span className="block text-[10px] text-muted-foreground">{preset.description}</span>
                                    </button>
                                )
                            })}
                            {encoderNote && (
                                <p className="px-2 pb-1 pt-1 text-[10px] text-muted-foreground" role="status">
                                    {encoderNote}
                                </p>
                            )}

                            {onUploadLimitChange && (
                                <UploadLimitMenu
                                    value={uploadLimit}
                                    onChange={onUploadLimitChange}
                                    headingClassName="sticky top-0 z-10 mt-2.5 border-t border-border/60 bg-popover px-2 pb-1.5 pt-2 text-[10px] font-bold uppercase tracking-widest text-primary"
                                />
                            )}

                            {onReceivePreferenceChange && (
                            <p className="sticky top-0 z-10 mt-2.5 border-t border-border/60 bg-popover px-2 pb-1.5 pt-2 text-[10px] font-bold uppercase tracking-widest text-primary">
                                O que você recebe
                            </p>
                            )}
                            {onReceivePreferenceChange && RECEIVE_ORDER.map((key) => {
                                const preset = RECEIVE_PRESETS[key]
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => onReceivePreferenceChange?.(key)}
                                        aria-pressed={receivePreference === key}
                                        className={cn(
                                            'w-full rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-accent text-xs',
                                            receivePreference === key && 'bg-accent font-semibold text-primary'
                                        )}
                                    >
                                        <span className="block font-medium text-foreground">{preset.label}</span>
                                        <span className="block text-[10px] text-muted-foreground">{preset.description}</span>
                                    </button>
                                )
                            })}

                            {canUseVoice && (
                                <>
                                    <p className="sticky top-0 z-10 mt-2.5 border-t border-border/60 bg-popover px-2 pb-1.5 pt-2 text-[10px] font-bold uppercase tracking-widest text-primary">
                                        Microfone & Áudio
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => onNoiseSuppressionChange(!noiseSuppression)}
                                        className={cn(
                                            'w-full rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-accent text-xs',
                                            noiseSuppression && 'bg-accent text-primary'
                                        )}
                                    >
                                        <span className="block font-medium text-foreground">Redução de Ruído</span>
                                        <span className="block text-[10px] text-muted-foreground">Filtra teclado e ruídos de fundo</span>
                                    </button>

                                    {microphones.length > 1 && microphones.map((device, index) => (
                                        <button
                                            key={device.deviceId}
                                            type="button"
                                            onClick={() => onMicChange(device.deviceId)}
                                            className={cn(
                                                'w-full truncate rounded-lg px-2 py-1.5 text-left text-xs transition-colors hover:bg-accent',
                                                micId === device.deviceId && 'bg-accent text-primary font-medium'
                                            )}
                                        >
                                            {device.label || `Microfone ${index + 1}`}
                                        </button>
                                    ))}
                                </>
                            )}

                            {canUseCamera && (
                                <>
                                    <p className="sticky top-0 z-10 mt-2.5 flex items-center justify-between border-t border-border/60 bg-popover px-2 pb-1.5 pt-2 text-[10px] font-bold uppercase tracking-widest text-primary">
                                        <span>Fundo da Câmera</span>
                                        {effectLoading && <Loader2 className="h-3 w-3 animate-spin" />}
                                    </p>

                                    <div className="grid grid-cols-3 gap-1 px-1 pb-1">
                                        {BACKGROUND_PRESETS.map((preset) => (
                                            <button
                                                key={preset.id}
                                                type="button"
                                                onClick={() => {
                                                    setUploadError(null)
                                                    onBackgroundChange(preset.effect)
                                                }}
                                                disabled={effectLoading}
                                                className={cn(
                                                    'flex flex-col items-center gap-1 rounded-lg p-1 transition-colors hover:bg-accent',
                                                    sameEffect(background, preset.effect) && 'bg-accent',
                                                    effectLoading && 'opacity-60'
                                                )}
                                            >
                                                <span
                                                    className={cn(
                                                        'h-7 w-full rounded-md border',
                                                        sameEffect(background, preset.effect) ? 'border-primary ring-1 ring-primary' : 'border-border/60'
                                                    )}
                                                    style={{ background: preset.swatch }}
                                                />
                                                <span className="w-full truncate text-center text-[9px] text-muted-foreground">
                                                    {preset.label}
                                                </span>
                                            </button>
                                        ))}

                                        <button
                                            type="button"
                                            onClick={() => fileRef.current?.click()}
                                            disabled={effectLoading}
                                            className="flex flex-col items-center gap-1 rounded-lg p-1 transition-colors hover:bg-accent"
                                        >
                                            <span className="flex h-7 w-full items-center justify-center rounded-md border border-dashed border-border/70">
                                                <ImagePlus className="h-3.5 w-3.5 text-muted-foreground" />
                                            </span>
                                            <span className="w-full truncate text-center text-[9px] text-muted-foreground">
                                                Imagem
                                            </span>
                                        </button>
                                    </div>

                                    <input
                                        ref={fileRef}
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={async (e) => {
                                            const file = e.target.files?.[0]
                                            e.target.value = ''
                                            if (!file) return
                                            try {
                                                setUploadError(null)
                                                onBackgroundChange(await readBackgroundFile(file))
                                            } catch (err) {
                                                setUploadError(err instanceof Error ? err.message : 'Imagem inválida.')
                                            }
                                        }}
                                    />
                                    {uploadError && (
                                        <p className="px-2 py-1 text-[10px] text-destructive">
                                            {uploadError}
                                        </p>
                                    )}

                                    {cameras.length > 1 && (
                                        <>
                                            <p className="sticky top-0 z-10 mt-2 border-t border-border/60 bg-popover px-2 pb-1.5 pt-2 text-[10px] font-bold uppercase tracking-widest text-primary">
                                                Dispositivo de Câmera
                                            </p>
                                            {cameras.map((device, index) => (
                                                <button
                                                    key={device.deviceId}
                                                    type="button"
                                                    onClick={() => onCameraChange(device.deviceId)}
                                                    className={cn(
                                                        'w-full truncate rounded-lg px-2 py-1.5 text-left text-xs transition-colors hover:bg-accent',
                                                        cameraId === device.deviceId && 'bg-accent text-primary font-medium'
                                                    )}
                                                >
                                                    {device.label || `Câmera ${index + 1}`}
                                                </button>
                                            ))}
                                        </>
                                    )}
                                </>
                            )}
                        </MriPopoverContent>
                    </MriPopover>
                </div>
            </div>
        </div>
    )
}

function useMicrophones(active: boolean): MediaDeviceInfo[] {
    const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([])

    useEffect(() => {
        let cancelled = false
        const refresh = () => {
            void listMicrophones().then((devices) => {
                if (!cancelled) setMicrophones(devices)
            })
        }
        refresh()

        const media = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined
        media?.addEventListener?.('devicechange', refresh)
        return () => {
            cancelled = true
            media?.removeEventListener?.('devicechange', refresh)
        }
    }, [active])

    return microphones
}

function useCameras(active: boolean): MediaDeviceInfo[] {
    const [cameras, setCameras] = useState<MediaDeviceInfo[]>([])

    useEffect(() => {
        let cancelled = false
        const refresh = () => {
            void listCameras().then((devices) => {
                if (!cancelled) setCameras(devices)
            })
        }
        refresh()

        const media = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined
        media?.addEventListener?.('devicechange', refresh)
        return () => {
            cancelled = true
            media?.removeEventListener?.('devicechange', refresh)
        }
    }, [active])

    return cameras
}

function sameEffect(a: BackgroundEffect, b: BackgroundEffect): boolean {
    if (a.kind !== b.kind) return false
    return a.kind === 'image' && b.kind === 'image' ? a.src === b.src : true
}
